import { AddCircle, Cancel, Edit } from "@mui/icons-material";
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  Autocomplete,
  Button,
  Field,
  FieldsContainer,
  Form,
  PageHeader,
  TextField,
} from "common";
import I18n from "i18n-js";
import { useState } from "react";
import LocationForm from "../setup/locations/LocationForm";
import { WaiverStatus } from "../waivers/waiverApi";
import { SharingFormItem } from "./sharingApi";
import useSharingForm, { UseSharingFormProps } from "./useSharingForm";

export interface SharingFormProps extends UseSharingFormProps {
  disabled?: boolean;
  defaultValues?: Partial<SharingFormItem>;
}

export default function SharingForm({
  id,
  afterSave,
  onClose,
  listItem,
  disabled,
  defaultValues,
}: SharingFormProps) {
  const {
    item,
    options,
    errors,
    loading,
    isDirty,
    isCreate,
    getIsDirty,
    getRequiredFields,
    onChange,
    save,
    isUpdate,
    saving,
    setAttributes,
    loadOptions,
  } = useSharingForm({
    id,
    afterSave,
    defaultValues,
  });
  const [nameIsEditable, setNameIsEditable] = useState(false);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const title = isCreate
    ? I18n.t("Sharing.addTitle")
    : I18n.t("Sharing.editTitle");
  return (
    <>
      <PageHeader title={title} onClose={onClose} />
      <DialogContent>
        <Form
          loading={loading}
          item={item}
          options={options}
          errors={errors}
          onChange={onChange}
          translationCategory="Sharing.attributes"
          isDirty={getIsDirty}
          requiredFields={getRequiredFields()}
          disabled={disabled}
        >
          <FieldsContainer>
            <Field fullWidth>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Autocomplete id="location_id" />
                </Box>
                <IconButton
                  onClick={() => setShowLocationForm(true)}
                  title={I18n.t("Location.addTitle")}
                >
                  <AddCircle />
                </IconButton>
              </Box>
            </Field>
            {!defaultValues?.waiver_ids && (
              <Field fullWidth>
                <Autocomplete
                  id="waiver_ids"
                  multiple
                  getOptionDisabled={(option: any) => {
                    return option.status === WaiverStatus.INACTIVE;
                  }}
                  renderTags={(tagValue, getTagProps) => {
                    return tagValue.map((option: any, index) => (
                      <Chip
                        {...(getTagProps({ index }) as any)}
                        sx={{
                          backgroundColor:
                            option.status === WaiverStatus.ACTIVE
                              ? "primary.main"
                              : "secondary.main",
                        }}
                        key={option.value}
                        label={option.label}
                      />
                    ));
                  }}
                />
              </Field>
            )}
            <Field fullWidth>
              <TextField
                id="name"
                disabled={!nameIsEditable}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setNameIsEditable(!nameIsEditable)}
                      >
                        {nameIsEditable ? <Cancel /> : <Edit />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Field>
            <Field fullWidth>
              <Autocomplete
                id="tags"
                multiple
                freeSolo
                options={options.tags?.map(t => t.label)}
                /*ChipProps={{
                  size: "small",
                }}*/
              />
            </Field>
          </FieldsContainer>
        </Form>
      </DialogContent>
      <DialogActions>
        <Button
          title={I18n.t("Sharing.share")}
          disabled={!isDirty}
          onClick={() => save()}
          loading={saving}
        />
      </DialogActions>

      <Dialog
        open={showLocationForm}
        onClose={() => setShowLocationForm(false)}
        maxWidth="sm"
        fullWidth
      >
        <LocationForm
          id="create"
          afterSave={newLocation => {
            setShowLocationForm(false);
            // Refresh location options and select the new location
            if (loadOptions) {
              loadOptions().then(() => {
                setAttributes({ location_id: newLocation.id });
              });
            } else if (options.location_id) {
              const newLocationOption = {
                value: newLocation.id,
                label: newLocation.name,
              };
              options.location_id.push(newLocationOption);
              setAttributes({ location_id: newLocation.id });
            }
          }}
          onClose={() => setShowLocationForm(false)}
        />
      </Dialog>
    </>
  );
}
